/**
 * This module manages the files model of the API which just manages the CRUD operations
 * for file entry metadata.
 *
 * For attachment to DB models and process choreography, see attachments.js
 * For files collection CRUD, see files.js
 * For uploading (to s3), see upload.js
 *
 * @file
 * @author Benjamin Listwon <ben@thebrandingfarm.com>
 * @author Jason Haddix <jason@thebrandingfarm.com>
 */

import api from '@/api'
import cloneDeep from 'lodash/cloneDeep'

import {
    VUEX_FILES_INDEX_REQUEST,
    VUEX_FILES_INDEX_SUCCESS,
	VUEX_FILES_INDEX_FAILURE,
	VUEX_FILE_SHOW_REQUEST,
    VUEX_FILE_SHOW_SUCCESS,
	VUEX_FILE_SHOW_FAILURE,
	VUEX_FILE_CREATE_REQUEST,
	VUEX_FILE_CREATE_SUCCESS,
	VUEX_FILE_CREATE_FAILURE,

	VUEX_FILE_UPDATE_REQUEST,

	VUEX_FILE_HANDLE_FILE_NOTIFICATION,
} from '@/store/constants/files'

import {
	VUEX_ATTACHMENT_QUEUE_MANAGER_FILE_CREATE_SUCCESS,
	VUEX_ATTACHMENT_QUEUE_MANAGER_FILE_CREATE_FAILURE
} from '@/store/constants/attachment_queue_manager'

import {
	VUEX_JOB_REPLACE_FILE_ON_COMMENT_ATTACHMENT,
	VUEX_JOB_REPLACE_FILE_ON_JOB_ATTACHMENT
} from '@/store/constants/jobs'

/**
 * Module state
 * @const
 */
const state = {
	// These are for general file work
	files: [],
	file:  {},

	// Record an error for later
	lastError: null,
}

/**
 * Module getters
 * @const
 */
const getters = {}

/**
 * Module action definitions
 * @const
 */
const actions = {

	/**
	 * Fetch a list of files
	 */
    [VUEX_FILES_INDEX_REQUEST]: async ({ commit }) => {
        commit(VUEX_FILES_INDEX_REQUEST)

        await api.get('/files/').then(async (response) => {
			await commit(VUEX_FILES_INDEX_SUCCESS, response.data.data)
		}).catch((err) => {
			commit(VUEX_FILES_INDEX_FAILURE, err)
		})
	},

	/**
	 * Fetch a single file by ID
	 */
	[VUEX_FILE_SHOW_REQUEST]: async ({ commit }, fileId) => {
        commit(VUEX_FILE_SHOW_REQUEST)

        await api.get(`/files/${fileId}`).then(async (response) => {
			await commit(VUEX_FILE_SHOW_SUCCESS, response.data.data)
		}).catch((err) => {
			commit(VUEX_FILE_SHOW_FAILURE, err)
		})
	},

	/**
	 * Create a file record
	 */
	[VUEX_FILE_CREATE_REQUEST]: async ({ commit, dispatch }, payload) => {
	    commit(VUEX_FILE_CREATE_REQUEST)
		await api.post(`/files`, payload).then(async (response) => {
			let responseData = response.data.data

			await commit(VUEX_FILE_CREATE_SUCCESS, responseData)

			// If we're doing this for the AQM
			if (responseData.filemeta && responseData.filemeta.hashId) {
				dispatch(VUEX_ATTACHMENT_QUEUE_MANAGER_FILE_CREATE_SUCCESS, responseData)
			}
		}).catch((err) => {
			commit(VUEX_FILE_CREATE_FAILURE, err)

			// TODO: get hashid in scope?
			dispatch(VUEX_ATTACHMENT_QUEUE_MANAGER_FILE_CREATE_FAILURE, err)
		})
	},


	[VUEX_FILE_UPDATE_REQUEST]: async ({ commit, dispatch }, payload) => {
		commit(VUEX_FILE_UPDATE_REQUEST)

		await api.patch(`/files/${payload.id}`, payload).then(async (response) => {

		}).catch((err) => {
			console.error(err)
		})
	},


	// When we get a file notification, we have to do a lot of scanning and logic to figure
	// out what to do and where, so I have moved it all out of echo to keep that clean.
	[VUEX_FILE_HANDLE_FILE_NOTIFICATION]: async({ commit, dispatch, state, rootState }, payload) => {
		let modelId = payload.model_id
		// TODO: Implement this once I think about strategy

		if (payload.action_type == SILA_MODEL_BROADCAST_ACTION_TYPE_UPDATE) {
			let jobAttachments = rootState.jobs.jobAttachments
			let jobComments = rootState.jobs.jobComments

			await dispatch(VUEX_FILE_SHOW_REQUEST, modelId)

			let fileCopy = cloneDeep(state.file)

			for (var attachmentIndex in jobAttachments) {
				if (jobAttachments[attachmentIndex].file_id && jobAttachments[attachmentIndex].file_id === modelId) {
					commit(VUEX_JOB_REPLACE_FILE_ON_JOB_ATTACHMENT, {
						attachmentIndex: attachmentIndex,
						file: fileCopy
					})
				}
			}

			for (var commentIndex in jobComments) {
				for (var attachmentIndex in jobComments[commentIndex].attachments) {
					if (jobComments[commentIndex].attachments[attachmentIndex].file_id && jobComments[commentIndex].attachments[attachmentIndex].file_id === modelId) {
						commit(VUEX_JOB_REPLACE_FILE_ON_COMMENT_ATTACHMENT, {
							commentIndex: commentIndex,
							attachmentIndex: attachmentIndex,
							file: fileCopy
						})
					}
				}
			}
		}
	},




	/*
		// Send S3 status to server, but do it from the AQM now
			// FILES-TODO: Remove this in favor of broadcaast


					let fileProps = {
			uri: payload.data.Location,
			upload_status: state.fileUploadStatus,
		}

		await api.patch(`/files/${state.fileUploadDbRecord.id}`, fileProps).then(() => {
			// dispatch(VUEX_FILE_SHOW_REQUEST, state.fileUploadDbRecord.id).then(() => {
			// 	if (payload.callback) {
			// 		payload.callback(state.fetchedFile)
			// 	}
			// })
		})

		*/
}

/**
 * Module mutation definitions
 * @const
 */
const mutations = {

	/**
	 * Get a single file object from the DB
	 */
	[VUEX_FILES_INDEX_REQUEST]: () => {},

	[VUEX_FILES_INDEX_SUCCESS]: (state, payload) => {
		state.files = payload
	},

	[VUEX_FILES_INDEX_FAILURE]: (state, error) => {
		state.lastError = error
	},

	/**
	 * Get a single file object from the DB
	 */
	[VUEX_FILE_SHOW_REQUEST]: () => {},

	[VUEX_FILE_SHOW_SUCCESS]: (state, payload) => {
		state.file = payload
	},

	[VUEX_FILE_SHOW_FAILURE]: (state, error) => {
		state.lastError = error
	},

	/**
	 * The DB object creation for new files
	 */
	[VUEX_FILE_CREATE_REQUEST]: () => {},

	[VUEX_FILE_CREATE_SUCCESS]: (state, payload) => {
		state.file = payload
	},

	[VUEX_FILE_CREATE_FAILURE]: (state, error) => {
		state.file = {}
		state.lastError = error
	},


	/**
	 * The DB object update for setting props on files
	 */
	[VUEX_FILE_UPDATE_REQUEST]: () => {},

}

/**
 * The Files module of our Vuex store.
 * @module store/modules/files
 */
export default {
  state,
  getters,
  actions,
  mutations,
}
